import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface Carrier {
  id: string;
  name: string;
  code: string;
  website: string;
  trackingUrlTemplate: string;
  active: boolean;
}

// Cache carriers to avoid repeated Firebase calls
let carriersCache: Carrier[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const fetchCarriers = async (): Promise<Carrier[]> => {
  // Return cached data if still fresh
  if (carriersCache && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return carriersCache;
  }

  try {
    const carriersRef = collection(db, 'carriers');
    const snapshot = await getDocs(carriersRef);
    
    const carriers: Carrier[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      carriers.push({
        id: doc.id,
        name: data.name,
        code: data.code,
        website: data.website,
        trackingUrlTemplate: data.trackingUrlTemplate,
        active: data.active
      });
    });
    
    // Cache the results
    carriersCache = carriers
      .filter(carrier => carrier.active)
      .sort((a, b) => a.name.localeCompare(b.name));
    cacheTimestamp = Date.now();
    
    return carriersCache;
  } catch (error) {
    console.error('Error fetching carriers:', error);
    // Fallback to hardcoded DHL and FedEx if Firebase fails
    const fallbackCarriers = [
      {
        id: 'dhl',
        name: 'DHL',
        code: 'dhl',
        website: 'dhl.com',
        trackingUrlTemplate: 'https://www.dhl.com/en/express/tracking.html?AWB={trackingNumber}',
        active: true
      },
      {
        id: 'fedex',
        name: 'FedEx',
        code: 'fedex',
        website: 'fedex.com',
        trackingUrlTemplate: 'https://www.fedex.com/apps/fedextrack/?tracknumbers={trackingNumber}',
        active: true
      }
    ];
    
    // Cache fallback data too
    carriersCache = fallbackCarriers;
    cacheTimestamp = Date.now();
    
    return fallbackCarriers;
  }
};

export const getCarrierByCode = async (code: string): Promise<Carrier | null> => {
  const carriers = await fetchCarriers();
  return carriers.find(carrier => carrier.code.toLowerCase() === code.toLowerCase()) || null;
};

export const generateCarrierTrackingUrl = (carrier: Carrier, trackingNumber: string): string => {
  return carrier.trackingUrlTemplate.replace('{trackingNumber}', trackingNumber);
}; 